import React, { useState } from 'react';
import { Check, Send, Calendar, Rocket, Target, TrendingUp, ShieldCheck, Clock, RefreshCw, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { CALENDLY_DECOUVERTE_URL } from '@/lib/config';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

/* ─── DATA ─────────────────────────────────────────── */

const TARGET_AUDIENCE = [
  {
    icon: Rocket,
    title: "Vendre en automatique",
    description: "Tu veux un système qui génère des ventes même quand tu ne travailles pas.",
  },
  {
    icon: Target,
    title: "Structurer ton offre",
    description: "Tu veux une offre claire, un message qui convertit et un parcours client fluide.",
  },
  {
    icon: TrendingUp,
    title: "Scaler sans publicité",
    description: "Tu veux grandir grâce à un écosystème organique, sans dépendre des ads.",
  },
];

const TIMELINE_STEPS = [
  {
    num: "01",
    label: "Semaines 1-2",
    title: "Stratégie",
    items: ["Positionnement différenciateur", "Message & storytelling de marque", "Stratégie de contenu sur 3 mois"],
  },
  {
    num: "02",
    label: "Semaines 3-4",
    title: "Construction",
    items: ["Tunnel de vente complet", "Séquences email automatisées", "10 visuels + captions copywritées"],
  },
  {
    num: "03",
    label: "Semaines 5-6",
    title: "Lancement",
    items: ["Automatisations (Airtable, Make, Brevo)", "Intégration écosystème complet", "Stratégie de lancement + handover"],
  },
];

const BEFORE_AFTER = {
  before: [
    "Tu postes sans stratégie",
    "Tes offres sont floues",
    "Tu perds des prospects à chaque étape",
    "Tu fais tout manuellement",
  ],
  after: [
    "Chaque contenu alimente ton tunnel",
    "Ton offre est irrésistible et claire",
    "Un système capture et convertit pour toi",
    "Tout est automatisé et connecté",
  ],
};

const REASSURANCE = [
  { icon: Clock, text: "Paiement en 3×" },
  { icon: Calendar, text: "Appel gratuit 30 min" },
  { icon: RefreshCw, text: "Satisfaite ou on ajuste" },
];

/* ─── FORM TYPES ───────────────────────────────────── */

interface FormData {
  name: string;
  email: string;
  website: string;
  objective: string;
  budget: string;
}

/* ─── COMPONENT ────────────────────────────────────── */

const SystemeGlow: React.FC = () => {
  useScrollReveal();
  const { toast } = useToast();

  const [formData, setFormData] = useState<FormData>({
    name: '', email: '', website: '', objective: '', budget: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.objective) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from('glow_leads').insert({
        name: formData.name,
        email: formData.email,
        website: formData.website || null,
        objective: formData.objective,
        budget: formData.budget || null,
      });

      if (error) throw error;

      toast({
        title: "🎉 Formulaire envoyé !",
        description: "Merci ! Tu peux maintenant réserver ton appel découverte.",
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Erreur envoi formulaire:', err);
      toast({
        title: "❌ Erreur d'envoi",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = formData.name && formData.email && formData.objective;

  const inputClasses = "w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-primary/50 transition-colors";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEOHead
        title="Système Glow — Écosystème digital clé en main | Glow Lab"
        description="6 semaines pour construire ton écosystème digital complet : positionnement, tunnel de vente, automatisations et contenu. Done-for-you, 3 800€."
        url="https://glow-architecte.com/systeme-glow"
      />
      <Header />

      <main className="flex-1">

        {/* ── HERO ────────────────────────────────── */}
        <div className="container mx-auto px-4 py-12 md:py-20">
          <div className="max-w-3xl mx-auto">
            <div className="reveal text-center">
              <div className="reveal-child w-12 h-px bg-primary/30 mx-auto mb-6" />
              <span className="reveal-child inline-block bg-primary text-primary-foreground text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-wider mb-4">
                Offre principale
              </span>
              <h1 className="reveal-child font-display text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.1]">
                Système Glow
              </h1>
              <p className="reveal-child text-muted-foreground text-lg md:text-xl mt-4 text-pretty">
                Un système digital clé en main pour vendre 24h/24, sans publicité.
              </p>
              <div className="reveal-child flex items-baseline justify-center gap-3 mt-4">
                <span className="text-rose-text font-display font-semibold text-2xl">3 800€</span>
                <span className="text-sm text-muted-foreground">ou 3× 1 300€</span>
              </div>
              <p className="reveal-child text-xs text-muted-foreground mt-2 uppercase tracking-wider">
                Done-for-you complet en 6 semaines
              </p>
            </div>
          </div>
        </div>

        {/* ── POUR QUI — bande off-white pleine largeur ── */}
        <div className="bg-muted py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="reveal">
                <h2 className="reveal-child font-display text-2xl md:text-3xl text-foreground text-center mb-8">
                  Pour qui c'est fait
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {TARGET_AUDIENCE.map((item, i) => (
                    <div
                      key={i}
                      className="reveal-child text-center p-6 border border-border rounded-xl bg-card shadow-sm hover-lift transition-all duration-300"
                    >
                      <item.icon className="w-6 h-6 text-rose-text mx-auto" strokeWidth={1.5} />
                      <p className="font-display text-lg mt-3 text-foreground">{item.title}</p>
                      <p className="text-muted-foreground text-sm mt-1">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── TIMELINE LIVRABLES — fond teinté rose ── */}
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-3xl mx-auto">
            <div className="reveal bg-[hsl(var(--primary)/0.03)] rounded-2xl p-6 md:p-10">
              <h2 className="reveal-child font-display text-2xl md:text-3xl text-foreground text-center mb-10">
                Ce que tu reçois
              </h2>
              <div className="space-y-6">
                {TIMELINE_STEPS.map((step, gi) => (
                  <div
                    key={gi}
                    className="reveal-child border-l-2 border-rose-text/40 pl-6 relative"
                  >
                    <div className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-rose-text" />
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-display text-3xl text-rose-text/50 font-semibold">{step.num}</span>
                      <span className="text-xs font-semibold uppercase tracking-wider text-foreground bg-card px-3 py-1 rounded-full">
                        {step.label}
                      </span>
                    </div>
                    <p className="font-display text-xl text-foreground mb-3">{step.title}</p>
                    <div className="space-y-2">
                      {step.items.map((item, ii) => (
                        <div key={ii} className="flex gap-3 items-start">
                          <Check className="w-4 h-4 text-rose-text flex-shrink-0 mt-0.5" strokeWidth={2} />
                          <span className="text-foreground text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── AVANT / APRÈS — carte "Après" en dark ── */}
        <div className="bg-muted py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="reveal">
                <h2 className="reveal-child font-display text-2xl md:text-3xl text-foreground text-center mb-10">
                  Avant vs Après
                </h2>
                <div className="reveal-child grid md:grid-cols-2 gap-6">
                  {/* Before — clair */}
                  <div className="p-6 rounded-xl border border-border bg-card">
                    <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">Sans système</p>
                    <div className="space-y-3">
                      {BEFORE_AFTER.before.map((item, i) => (
                        <div key={i} className="flex gap-3 items-start">
                          <X className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                          <span className="text-muted-foreground text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* After — dark */}
                  <div className="p-6 rounded-xl bg-[hsl(var(--dark))] text-[hsl(var(--white))]">
                    <p className="text-xs font-semibold uppercase tracking-wider text-rose-text mb-4">Avec Système Glow</p>
                    <div className="space-y-3">
                      {BEFORE_AFTER.after.map((item, i) => (
                        <div key={i} className="flex gap-3 items-start">
                          <Check className="w-4 h-4 text-rose-text flex-shrink-0 mt-0.5" strokeWidth={2} />
                          <span className="text-[hsl(var(--white)/0.85)] text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── RÉASSURANCE — fond accent léger ── */}
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-3xl mx-auto">
            <div className="reveal">
              <div className="reveal-child bg-[hsl(var(--primary)/0.05)] rounded-xl py-5 px-6 flex flex-wrap justify-center gap-6">
                {REASSURANCE.map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-foreground">
                    <item.icon className="w-4 h-4 text-rose-text" strokeWidth={1.5} />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── CTA — ouvre la modale formulaire ── */}
        <div className="container mx-auto px-4 pb-16">
          <div className="max-w-3xl mx-auto">
            <div className="reveal text-center">
              <h2 className="reveal-child font-display text-2xl md:text-3xl text-foreground mb-3">
                Prête à lancer ton système ?
              </h2>
              <p className="reveal-child text-muted-foreground text-sm mb-8">
                Réserve un appel découverte gratuit de 30 minutes pour voir si le Système Glow est fait pour toi.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="reveal-child inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold py-4 px-8 rounded-xl hover:scale-[1.02] transition-all duration-300 shadow-[0_4px_20px_hsl(var(--primary)/0.3)]"
              >
                Réserver mon appel découverte
                <Calendar className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* ── MODALE FORMULAIRE ── */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Réserver un appel découverte</DialogTitle>
              <DialogDescription>
                Remplis ce formulaire rapide. Tu pourras ensuite réserver ton créneau.
              </DialogDescription>
            </DialogHeader>

            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Ton prénom <span className="text-rose-text">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Marie"
                      required
                      className={inputClasses}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">
                      Ton email <span className="text-rose-text">*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      placeholder="marie@exemple.com"
                      required
                      className={inputClasses}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Ton site ou Instagram actuel
                  </label>
                  <input
                    type="text"
                    value={formData.website}
                    onChange={(e) => handleChange('website', e.target.value)}
                    placeholder="https://... ou @moncompte"
                    className={inputClasses}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Quel est ton objectif principal ? <span className="text-rose-text">*</span>
                  </label>
                  <textarea
                    value={formData.objective}
                    onChange={(e) => handleChange('objective', e.target.value)}
                    placeholder="Ex : Je veux lancer ma formation en ligne et automatiser mes ventes..."
                    rows={3}
                    required
                    className={`${inputClasses} resize-none`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1.5">
                    Budget envisagé
                  </label>
                  <select
                    value={formData.budget}
                    onChange={(e) => handleChange('budget', e.target.value)}
                    className={inputClasses}
                  >
                    <option value="">Sélectionner</option>
                    <option value="3800">3 800€ (paiement unique)</option>
                    <option value="3x1300">3× 1 300€ (3 mensualités)</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={!isValid || isSubmitting}
                  className="w-full bg-primary text-primary-foreground font-semibold py-4 rounded-xl hover:scale-[1.02] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_4px_20px_hsl(var(--primary)/0.3)]"
                >
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                  {!isSubmitting && <Send className="w-4 h-4" />}
                </button>
              </form>
            ) : (
              <div className="py-4">
                <div className="text-center mb-6">
                  <div className="w-12 h-12 rounded-full bg-rose-text/10 flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-rose-text" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl text-foreground mb-2">
                    Merci {formData.name} !
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Réserve maintenant ton appel découverte gratuit de 30 minutes.
                  </p>
                </div>

                {CALENDLY_DECOUVERTE_URL ? (
                  <div className="rounded-lg overflow-hidden border border-border">
                    <iframe
                      src={CALENDLY_DECOUVERTE_URL}
                      width="100%"
                      height="500"
                      frameBorder="0"
                      title="Calendly — Appel Découverte"
                      className="w-full"
                    />
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed border-border bg-background p-12 text-center">
                    <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-muted-foreground text-sm">
                      Le calendrier de réservation sera disponible très bientôt.
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      En attendant, je te recontacte par email à {formData.email}
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

      </main>

      <Footer />
    </div>
  );
};

export default SystemeGlow;
